import React, { useEffect, useState, useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';
import classNames from 'classnames';
import { useRouter } from 'next/router';

import { Loader } from '@/components/ui';
import { hooks as miniMarketHooks } from '@/domains/mini-market';

import EshopItems from '../EshopItems/EshopItems';
import ProductFilter from '../Filter/ProductFilter';

import styles from './EshopCard.module.scss';

const EshopCard = () => {
  const { lang } = useTranslation();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const observer = useRef();
  const loadMoreRef = useRef(null);
  const limitPerPage = 6;
  const router = useRouter();

  const { data, isLoading } = miniMarketHooks.useGetProductList({
    lang,
    limit: limitPerPage,
    page,
    brands: router.query.brands,
    colors: router.query.colors,
    storages: router.query.storages,
  });

  const [products, setProducts] = useState([]);

  // Reset products and page when query parameters change
  useEffect(() => {
    setProducts([]);
    setPage(1);
  }, [router.query.brands, router.query.colors, router.query.storages]);

  // Fetch products when data or isLoading changes
  useEffect(() => {
    if (page === 1) {
      setProducts(data);
    } else {
      setProducts((prevProducts) => [...prevProducts, ...data]);
    }

    setHasMore(data?.length >= limitPerPage);
    setLoading(isLoading);
  }, [data, isLoading]);

  // Handle infinite scrolling
  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        if (data?.length >= limitPerPage) {
          setPage((prevPage) => prevPage + 1);
        }
      }
    });

    if (loadMoreRef.current) {
      observer.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [loading, hasMore, data]);

  return (
    <div
      className={classNames(['wrapper', styles.products_container])}
      itemScope
      itemType='https://schema.org/Thing'
    >
      <div className={styles.content_wrapper}>
        <ProductFilter />
        <div className={styles.card_wrapper}>
          {products?.map((product) => (
            <EshopItems key={product.id} product={product} />
          ))}
        </div>
      </div>
      {loading && <Loader className={styles.loading} />}
      <div ref={loadMoreRef}></div>
    </div>
  );
};

export default EshopCard;
